import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Card from "#/components/shared/card";
import ThemedSpinner from "#/components/shared/spinner";
import Typography from "#/components/shared/typography";
import { useGallery } from "#/hooks/useGallery";
import { useState } from "react";
import FileStoreList from "./FileStoreList";

const DEFAULT_PAGE_SIZE = 25;

export function FileStoreLayout() {
  const [page, setPage] = useState(1);
  const { images, isLoading, error, isFetchingMore, loadMore } = useGallery();

  const files = images ?? [];
  const total = files.length;
  const totalPages = Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE));
  const hasNext = page < totalPages;
  const hasPrev = page > 1;

  const paginatedFiles = files.slice(
    (page - 1) * DEFAULT_PAGE_SIZE,
    page * DEFAULT_PAGE_SIZE
  );

  const handleNext = () => {
    if (hasNext) {
      setPage((prev) => prev + 1);
    } else {
      loadMore();
    }
  };

  const handlePrev = () => {
    if (page > 1 && hasPrev) {
      setPage((prev) => Math.max(1, prev - 1));
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ThemedSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card variant="cream" elevation={1} className="p-6">
          <Typography variant="h4" className="text-terracotta">
            Failed to load files
          </Typography>
          <Typography variant="body2" className="text-brown-700">
            {error instanceof Error ? error.message : "Please try again later."}
          </Typography>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <header className="space-y-2">
        <Typography variant="h2" className="text-terracotta">
          Medical File Library
        </Typography>
        <Typography variant="body1" className="text-brown-700">
          View files uploaded to Harper and jump directly into the associated
          medical records.
        </Typography>
        {totalPages > 1 && (
          <Typography variant="caption" className="text-brown-600">
            Showing page {page} of {totalPages} · {total} files total
          </Typography>
        )}
      </header>

      <FileStoreList files={paginatedFiles} />

      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-lg border border-cream-200 bg-cream-50 p-4">
          <SubmitButton
            type="button"
            size="sm"
            color="cream"
            hideArrow
            onClick={handlePrev}
            isDisabled={!hasPrev || page === 1 || isFetchingMore}
          >
            Previous
          </SubmitButton>
          <Typography variant="body2" className="text-brown-700">
            Page {page} of {totalPages}
            {isFetchingMore ? " · Updating…" : ""}
          </Typography>
          <SubmitButton
            type="button"
            size="sm"
            color="cream"
            hideArrow
            onClick={handleNext}
            isDisabled={!hasNext || isFetchingMore}
          >
            Next
          </SubmitButton>
        </div>
      )}
    </div>
  );
}

export default FileStoreLayout;
